/**
 * One artefact per mail client, built from the ONE fragment `renderSignatureHtml` produces.
 *
 * The fragment is identical in every artefact — only the envelope around it differs — so a
 * client-specific fix can never make one client's signature diverge from another's. How each
 * artefact is installed is on the page and in `docs/architecture/email-signatures.md`.
 *
 * | Client                  | Artefact                              | Why that and not a paste            |
 * | ----------------------- | ------------------------------------- | ----------------------------------- |
 * | Apple Mail (macOS)      | rich-text copy (not from Safari)      | fallback: the `.mailsignature` file |
 * | Apple Mail (iOS)        | rich-text copy                        | no file access on iOS               |
 * | Thunderbird             | `.html` file, "signature from a file" | the file is sent byte for byte      |
 * | Outlook (classic)       | `.htm` + `.rtf` + `.txt` in Signatures| Outlook's own on-disk format        |
 * | Outlook (new / web)     | rich-text copy                        | signatures live in the mailbox      |
 * | Mobile apps             | plain text                            | their signature field is plain text |
 */

import {
  normaliseField,
  renderSignatureHtml,
  renderSignatureText,
  signatureContactRows,
  signatureDisclaimerLines,
  signatureLegalLines,
  type SignatureInput,
} from './signature';
import { COMPANY_IDENTITY } from '@easy-invoices/types/company';

/**
 * The file name every download shares, without extension — e.g. `Easy Invoices - Erik Arens`.
 *
 * Classic Outlook pairs the `.htm`, `.rtf` and `.txt` of one signature BY BASE NAME and lists the
 * base name as the signature's title, so all three must match exactly. Reduced to ASCII letters,
 * digits, spaces and hyphens: an umlaut in a file name is where Windows, macOS and a zip tool
 * start disagreeing.
 */
export function signatureFileBase(input: SignatureInput): string {
  const name = normaliseField(input.fullName)
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .replace(/ß/g, 'ss')
    .replace(/[^A-Za-z0-9 -]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return name
    ? `${COMPANY_IDENTITY.productName} - ${name}`
    : COMPANY_IDENTITY.productName;
}

/**
 * The fragment wrapped as a complete HTML document — Thunderbird's signature file and classic
 * Outlook's `.htm`.
 *
 * The `<body>` carries NO margin or style of its own on purpose: Thunderbird inserts the body's
 * CONTENT, and Outlook reads the body into its signature editor, so anything on the body element
 * would be lost in one client and kept in the other. The charset is declared although the content
 * is ASCII, because Outlook otherwise guesses from the Windows code page.
 */
export function signatureHtmlDocument(input: SignatureInput): string {
  return [
    '<!DOCTYPE html>',
    '<html>',
    '<head>',
    '<meta http-equiv="Content-Type" content="text/html; charset=utf-8" />',
    '<meta name="color-scheme" content="light" />',
    `<title>${signatureFileBase(input)}</title>`,
    '</head>',
    '<body>',
    renderSignatureHtml(input),
    '</body>',
    '</html>',
    '',
  ].join('\r\n');
}

/**
 * What goes into Apple Mail's `.mailsignature` file BELOW its header lines — the FALLBACK install,
 * for when a pasted signature makes Mail attach the logo after all.
 *
 * Only the fragment: the file's own headers (`Content-Type: text/html; charset=us-ascii`,
 * `Message-Id`, `Mime-Version`) belong to Mail and are kept, which is why the renderer writes
 * nothing outside printable ASCII.
 */
export function appleMailSignatureBody(input: SignatureInput): string {
  return renderSignatureHtml(input);
}

/** Escapes text for RTF: control characters, then everything outside ASCII as `\uN?`. */
export function escapeRtf(value: string): string {
  let out = '';
  // By UTF-16 code unit, which is what RTF's `\u` counts in: an astral character is its two
  // surrogates, each written as its own signed 16-bit value.
  for (let index = 0; index < value.length; index += 1) {
    const unit = value.charCodeAt(index);
    const char = value[index];
    if (char === '\\' || char === '{' || char === '}') out += `\\${char}`;
    else if (unit > 0x7e || unit < 0x20) {
      out += `\\u${unit > 0x7fff ? unit - 0x10000 : unit}?`;
    } else out += char;
  }
  return out;
}

/** An RTF hyperlink field. */
function rtfLink(href: string, text: string): string {
  return (
    `{\\field{\\*\\fldinst{HYPERLINK "${escapeRtf(href)}"}}` +
    `{\\fldrslt{\\cf4 ${escapeRtf(text)}}}}`
  );
}

/**
 * Classic Outlook's `.rtf` — what it inserts when a mail is composed in Rich Text format. Same
 * lines, same order, same colours as the HTML; no image, because an RTF signature image would
 * have to be embedded, and an embedded image is an attachment on every mail.
 */
export function signatureRtf(input: SignatureInput): string {
  const fullName = normaliseField(input.fullName);
  const jobTitle = normaliseField(input.jobTitle);
  const lines: string[] = [
    `{\\b\\cf1\\fs30 ${escapeRtf(fullName)}}\\par`,
    ...(jobTitle ? [`{\\cf2\\fs26 ${escapeRtf(jobTitle)}}\\par`] : []),
    `{\\b\\fs28\\cf1 Easy }{\\b\\fs28\\cf5 Invoices}\\par`,
    '\\par',
    ...signatureContactRows(input).map(
      (row) =>
        `{\\cf3\\fs24 ${escapeRtf(row.label)}}\\tab ${rtfLink(row.href, row.value)}\\par`,
    ),
  ];
  if (input.showLegal) {
    lines.push(
      '\\par',
      ...signatureLegalLines(input.locale).map(
        (line) => `{\\cf3\\fs22 ${escapeRtf(line)}}\\par`,
      ),
    );
  }
  lines.push(
    '\\par',
    ...signatureDisclaimerLines(input.locale).map(
      (line) => `{\\cf3\\fs22 ${escapeRtf(line)}}\\par`,
    ),
  );
  return [
    '{\\rtf1\\ansi\\ansicpg1252\\deff0',
    '{\\fonttbl{\\f0\\fswiss\\fcharset0 Arial;}}',
    // 1 ink, 2 body, 3 muted, 4 link, 5 accent — `SIGNATURE_COLORS`.
    '{\\colortbl;\\red17\\green24\\blue39;\\red75\\green85\\blue99;\\red107\\green114\\blue128;\\red15\\green118\\blue110;\\red20\\green184\\blue166;}',
    '\\f0\\fs26\\tx1300',
    ...lines,
    '}',
    '',
  ].join('\r\n');
}

/**
 * Classic Outlook's `.txt`: the plain-text rendering with Windows line endings, behind a UTF-8 BOM.
 *
 * This is the one artefact that can carry a byte outside ASCII — the HTML is numeric-reference
 * encoded, the RTF escapes to `\uN?` and the file base name is folded — because the name and job
 * title reach it verbatim. Outlook decodes a signature `.txt` in the system code page unless a BOM
 * says otherwise, which turns `Jürgen` into `JÃ¼rgen` in every plain-text reply.
 */
export function signatureOutlookText(input: SignatureInput): string {
  return `\ufeff${renderSignatureText(input).split('\n').join('\r\n')}\r\n`;
}

/** How the preview frame shows the signature. */
export interface SignaturePreviewOptions {
  /** Refuse every image, the way classic Outlook does until the reader allows them. */
  readonly blockImages: boolean;
  /**
   * Approximate a client that force-inverts light mail (Gmail and Outlook apps in dark mode):
   * everything inverted and hue-rotated back, images excluded. An APPROXIMATION — each client
   * inverts differently — which is why the page labels it as one.
   */
  readonly darkMode: boolean;
}

/**
 * The preview frame's document. Only the frame's envelope carries CSS; the signature inside is the
 * exact fragment the clients receive. Blocked images are enforced by a CSP inside the frame rather
 * than by editing the markup, so what the operator sees is the real alt-text fallback.
 */
export function signaturePreviewDocument(
  fragment: string,
  options: SignaturePreviewOptions,
): string {
  const csp = options.blockImages
    ? `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src 'none'; style-src 'unsafe-inline'" />`
    : `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src https:; style-src 'unsafe-inline'" />`;
  const dark = options.darkMode
    ? '<style>html{background:#ffffff;filter:invert(1) hue-rotate(180deg);}img{filter:invert(1) hue-rotate(180deg);}</style>'
    : '';
  return (
    `<!DOCTYPE html><html><head><meta charset="utf-8" />${csp}${dark}</head>` +
    `<body style="margin:0;padding:20px;background:#ffffff;">${fragment}</body></html>`
  );
}
